import { useEffect, useMemo } from 'react';
import { addDays, getDay, setDay, startOfWeek } from 'date-fns';
import { findKey, get, isEqual, toLower, upperFirst, find, last, range } from 'lodash-es';
import PropTypes from 'prop-types';
import { Controller } from 'react-hook-form';
import { BehaviorSubject, filter, ReplaySubject } from 'rxjs';
import { useObservableState, useSubscription } from 'observable-hooks';
import getLocale, { getWeekDayWide, useLocale } from '../../../../utils/date-fns';
import Select from '../../../form/Select';
import TimePicker from '../../../form/TimePicker';

import { defaultPropTypes } from '../../utils';
import { useProfile } from '../../../../profile';
import { timeToDate } from '../../../../utils/time';

import arrowBentDown from './arrow-bent-down.svg';

const MEASURE = Object.freeze({
    ALL_DAY: 'ALL_DAY',
    NEVER: 'NEVER',
    CUSTOM: 'CUSTOM',
});

const translations = {
    [MEASURE.ALL_DAY]: gettext('MEASURE_ALL_DAY'),
    [MEASURE.NEVER]: gettext('MEASURE_NEVER'),
    [MEASURE.CUSTOM]: gettext('MEASURE_CUSTOM'),
};

const startOfTheDay = '00:00';
const endOfTheDay = '23:59';

const presets = {
    [MEASURE.ALL_DAY]: {
        enable: startOfTheDay,
        disable: endOfTheDay,
    },
    [MEASURE.NEVER]: {
        enable: startOfTheDay,
        disable: startOfTheDay,
    },
    [MEASURE.CUSTOM]: {
        enable: '08:00',
        disable: '18:00',
    },
};

function getPresetForDaySchedule(daySchedule) {
    return findKey(presets, (preset) => isEqual(preset, daySchedule)) || MEASURE.CUSTOM;
}
class ScheduleManager {
    constructor(locale) {
        // Schedule data that comes from the API/form state.
        this.input$ = new BehaviorSubject({});

        // The actual schedule we want to display and manipulate.
        this.schedule$ = new BehaviorSubject({});

        // The output subject. Using a `ReplaySubject(1)` ensures it does not have a default
        // value and only emits after the first changed value, compared to the input, is received.
        this.output$ = new ReplaySubject(1);

        // Every time new input is received, it is written directly into the schedule.
        // We keep track of the input and schedule separately to detect changes between
        // them. If there is no change, we avoid calling onChange unnecessarily, which
        // could potentially cause an infinite incoming value > onChange feedback loop.
        this.input$.subscribe(this.schedule$);

        this.schedule$
            .pipe(
                // There is no need to emit output when the output is the same as the input.
                filter((output) => !isEqual(output, this.input$.getValue()))
            )
            .subscribe(this.output$);

        const localeEn = getLocale('en');

        const firstDOW = startOfWeek(new Date(), { locale });

        this.week = range(7).map((i) => {
            const weekDay = getDay(addDays(firstDOW, i));
            return {
                // E.g.: monday.
                weekDay: toLower(getWeekDayWide(localeEn, weekDay)),
                // E.g.: Monday.
                displayName: upperFirst(getWeekDayWide(locale, weekDay)),
                // E.g.: 1.
                number: weekDay,
            };
        });

        this.lastDOW = last(this.week).weekDay;
    }

    setScheduleForWeekDay(weekDay, daySchedule) {
        const current = this.schedule$.getValue();

        this.schedule$.next({
            ...current,
            [weekDay]: {
                ...current[weekDay],
                ...daySchedule,
            },
        });
    }

    getWeekDayNumber(weekDay) {
        return find(this.week, ['weekDay', weekDay]).number;
    }

    getWeekDayByNumber(number) {
        return find(this.week, ['number', number]).weekDay;
    }

    getNextWeekDayNumber(weekDay) {
        return getDay(addDays(setDay(new Date(), this.getWeekDayNumber(weekDay)), 1));
    }

    getNextWeekDay(weekDay) {
        return this.getWeekDayByNumber(this.getNextWeekDayNumber(weekDay));
    }

    copyToNextDay(weekDay) {
        this.setScheduleForWeekDay(
            this.getNextWeekDay(weekDay),
            get(this.schedule$.getValue(), weekDay)
        );
    }

    // Method to integrate this class with React components.
    static useManager(value, onChange) {
        // ESLint will complain that hooks cannot be called in
        // a class component, but we are not doing that.
        /* eslint-disable react-hooks/rules-of-hooks */
        const locale = useLocale();
        const manager = useMemo(() => new this(locale), [locale]);

        // Push the incoming values to the RowManager.
        useEffect(() => {
            manager.input$.next(value);
        }, [manager, value]);

        // Push the outgoing values from the RowManager to the onChange callback.
        useSubscription(manager.output$, onChange);

        const schedule = useObservableState(manager.schedule$, {});

        return { schedule, manager };
        /* eslint-enable */
    }
}

const useScheduleControlPropTypes = {
    schedule: PropTypes.object,
    manager: PropTypes.instanceOf(ScheduleManager),
};

function DropDown({ schedule, manager, weekDay }) {
    return (
        <Select
            aria-label={weekDay}
            value={getPresetForDaySchedule(get(schedule, weekDay))}
            onChange={(event) => {
                const selected = event.target.value;
                const preset = get(presets, selected);
                if (preset) {
                    manager.setScheduleForWeekDay(weekDay, preset);
                }
            }}
            data={Object.keys(MEASURE).map((key) => [key, translations[key]])}
        >
            {Object.keys(MEASURE).map((key) => (
                <option key={key} value={key}>
                    {translations[key]}
                </option>
            ))}
        </Select>
    );
}
DropDown.propTypes = {
    ...useScheduleControlPropTypes,
    weekDay: PropTypes.string.isRequired,
};

function TimeInput({ schedule, manager, weekDay, part, is24Hour }) {
    const value = get(schedule, [weekDay, part]);
    const value2 = value && timeToDate(value);

    return (
        <TimePicker
            onChange={(newValue) => {
                if (newValue !== null) {
                    manager.setScheduleForWeekDay(weekDay, {
                        [part]: newValue,
                    });
                }
            }}
            value={value2}
            ariaLabel={`${weekDay}-${part}`}
            disabled={getPresetForDaySchedule(get(schedule, weekDay)) !== MEASURE.CUSTOM}
            is24Hour={is24Hour}
        />
    );
}

TimeInput.propTypes = {
    ...useScheduleControlPropTypes,
    weekDay: PropTypes.string.isRequired,
    part: PropTypes.oneOf(['enable', 'disable']),
};

function ScheduleTable({ schedule: incomingSchedule, onChange, is24Hour }) {
    const { schedule, manager } = ScheduleManager.useManager(incomingSchedule, onChange);

    return (
        // The ID is needed for the walkthrough.
        <table className="text-gray-500" id="measurement_schedule">
            <tbody>
                {manager.week.map(({ weekDay, displayName }) => (
                    <tr key={weekDay}>
                        <td className="pr-5">
                            <label className="text-xs">{gettext(displayName)}</label>
                            <DropDown schedule={schedule} manager={manager} weekDay={weekDay} />
                        </td>
                        <td className="pr-5">
                            <label className="text-xs">{`${gettext('FROM')}:`}</label>
                            <TimeInput
                                schedule={schedule}
                                manager={manager}
                                weekDay={weekDay}
                                part="enable"
                                is24Hour={is24Hour}
                            />
                        </td>
                        <td className="pr-5">
                            <label className="text-xs">{`${gettext('UNTIL')}:`}</label>
                            <TimeInput
                                schedule={schedule}
                                manager={manager}
                                weekDay={weekDay}
                                part="disable"
                                is24Hour={is24Hour}
                            />
                        </td>
                        <td className="relative">
                            {weekDay !== manager.lastDOW && (
                                <a
                                    title={gettext(upperFirst(weekDay))}
                                    onClick={() => manager.copyToNextDay(weekDay)}
                                    className="absolute -bottom-6 w-auto"
                                >
                                    <img src={arrowBentDown} className="max-w-5 opacity-30" />
                                </a>
                            )}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}
ScheduleTable.propTypes = {
    schedule: PropTypes.object.isRequired,
    onChange: PropTypes.func,
    is24Hour: PropTypes.bool,
};

export function ScheduleField({ fieldOptions }) {
    const { name, settings } = fieldOptions;
    const { use24HourNotation } = useProfile();

    return (
        <Controller
            name={name}
            defaultValue={settings.default}
            render={({ field: { value, onChange } }) => (
                <ScheduleTable schedule={value} onChange={onChange} is24Hour={use24HourNotation} />
            )}
        />
    );
}
ScheduleField.propTypes = defaultPropTypes;
